import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import _get from 'lodash/get'
import _isString from 'lodash/isString'
import _replace from 'lodash/replace'
import _trimEnd from 'lodash/trimEnd'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import { selectAuth } from '../../services/auth/selectors'
import { stripMarkdownForTTS } from '../../services/markdown/stripMarkdown'
import { get as getConfig } from '../../config/index'
import STTTTSService from '../../services/stt-tts/index'
import { EN } from '../../services/languages/constants'
import {
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_SPEECH_TO_TEXT_GA4_EVENT,
} from '../../services/event-tracking/index'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import { usePostHogEvent } from '../PostHog/index'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { useOracleAgentChatSubscriptionSubscription } from './queries.generated'
import { computeVoiceModeState, createVoiceModeInputs, type VoiceModeTranslations } from './voiceModeState'
import {
  createOracleSubscriptionInput,
  VOICE_CONFIG,
  DEFAULT_MOBILE_TALKING_THRESHOLD,
  CHAT_MODE,
} from './oracleChatSessionConfig'
import { DEFAULT_PERSONALITY_ID } from './personalityFilter'
import { getUserTimezone, RateLimitModeState } from './rateLimitUtils'
import OracleVoiceModeGlowIcon from './OracleVoiceModeGlowIcon'
import OracleMicrophoneButton from './OracleMicrophoneButton'
import { OracleErrorToast } from '../OracleCommons/OracleErrorToast'

const config = getConfig()
// trim the trailing slash from the speech server, as web app config appends it,
// and STT-TTS doesn't expect it
const speechServer = _trimEnd(_get(config, ['servers', 'brooklyn']), '/')

// Helper function to clean text for TTS by removing emojis and markdown
const cleanTextForTTS = (text: string): string => {
  if (!_isString(text)) {
    return ''
  }

  // First remove all emojis using surrogate pairs and common emoji ranges
  // This covers most common emojis (faces, objects, animals, symbols, etc.)
  const withoutEmojis = _replace(text, /[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF]/g, '')

  // Then remove markdown formatting
  return stripMarkdownForTTS(withoutEmojis)
}

interface OracleVoiceChatProps {
  onExit?: () => void
  currentSessionId?: string | null
  personalityId?: number
  location?: { search: string }
  onSessionReceived?: (sessionId: string) => void
  voiceRateLimitState?: RateLimitModeState
}

const OracleVoiceChat: React.FC<OracleVoiceChatProps> = ({
  onExit,
  currentSessionId,
  personalityId,
  location,
  onSessionReceived,
  voiceRateLimitState,
}) => {
  // Get user timezone for rate limiting
  const userTimezone = useMemo(() => getUserTimezone(), [])

  // Localized text - consolidated into single object
  const translations: VoiceModeTranslations = {
    processingHeader: useTranslatedText({ keys: ['voiceMode', 'processingHeader'] }),
    processingSubheader: useTranslatedText({ keys: ['voiceMode', 'processingSubheader'] }),
    headerAvailable: useTranslatedText({ keys: ['voiceMode', 'headerAvailable'] }),
    subheaderAvailable: useTranslatedText({ keys: ['voiceMode', 'subheaderAvailable'] }),
    headerListening: useTranslatedText({ keys: ['voiceMode', 'headerListening'] }),
    headerSharingWisdom: useTranslatedText({ keys: ['voiceMode', 'headerSharingWisdom'] }),
    mutedHeader: useTranslatedText({ keys: ['voiceMode', 'mutedHeader'] }),
    mutedSubheader: useTranslatedText({ keys: ['voiceMode', 'mutedSubheader'] }),
  }
  const muteTooltipText = useTranslatedText({ keys: ['voiceMode', 'muteTooltip'] })
  const exitVoiceChatLabel = useTranslatedText({ keys: ['voiceMode', 'exitVoiceChat'] })
  const closeTooltipLabel = useTranslatedText({ keys: ['voiceMode', 'closeTooltip'] })

  const [isListening, setIsListening] = useState(false)
  const [voiceError, setVoiceError] = useState<string | null>(null)
  const [showErrorToast, setShowErrorToast] = useState(false)
  const [debugErrors, setDebugErrors] = useState<string[]>([])
  const [receivedTexts, setReceivedTexts] = useState<string[]>([])
  const [lastReceivedText, setLastReceivedText] = useState<string>('')
  // Removed: lastAiResponse - no longer needed with chunked TTS
  const [showDebugInfo, setShowDebugInfo] = useState(true)
  const [accumulatedText, setAccumulatedText] = useState<string>('')
  const [subscriptionInput, setSubscriptionInput] = useState<
    ReturnType<typeof createOracleSubscriptionInput> | null
  >(null)
  const [isWaitingForResponseFromAi, setIsWaitingForResponseFromAi] = useState(false)
  // Removed: shouldRefetchForTTS, currentChatSessionIdForTTS - no longer needed
  const [isMuted, setIsMuted] = useState(false)
  const [showTooltip, setShowTooltip] = useState(true)

  // Chunking constraints
  const MIN_WORDS_PER_CHUNK = 2
  const getWordCount = (s: string) => {
    if (!s) return 0
    const tokens = s.trim().split(/\s+/)
    return tokens.filter((t) => /[A-Za-z0-9]/.test(t)).length
  }

  // Use ref to store session ID for immediate access in event handlers
  const localSessionIdRef = useRef<string | null>(currentSessionId)

  // Sync local session ID when prop changes
  useEffect(() => {
    localSessionIdRef.current = currentSessionId
  }, [currentSessionId])

  // STT service state tracking
  const [isTranslatingSTT, setIsTranslatingSTT] = useState(false)
  const [userIsTalking, setUserIsTalking] = useState(false)
  const [isTranslatingTTS, setIsTranslatingTTS] = useState(false)
  const [isSpeakingState, setIsSpeakingState] = useState(false)

  // Exit voice mode if rate limited - wait for TTS to finish first
  const ttsCompleteHandlerRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    if (voiceRateLimitState?.rateLimited && onExit) {
      // If currently speaking or translating TTS, wait for it to complete
      if (isSpeakingState || isTranslatingTTS) {
        // Clean up any previous handler before adding a new one
        if (ttsCompleteHandlerRef.current && sttServiceRef.current) {
          sttServiceRef.current.off('ttsQueueCompleted', ttsCompleteHandlerRef.current)
        }

        // Set up listener for when TTS completes
        const handleTTSComplete = () => {
          if (onExit && sttServiceRef.current) {
            sttServiceRef.current.off('ttsQueueCompleted', handleTTSComplete)
            ttsCompleteHandlerRef.current = null
            // Small delay to ensure TTS fully finishes
            setTimeout(() => {
              onExit()
            }, 100)
          }
        }

        ttsCompleteHandlerRef.current = handleTTSComplete

        if (sttServiceRef.current) {
          sttServiceRef.current.on('ttsQueueCompleted', handleTTSComplete)
        }

        return () => {
          // Cleanup listener if component unmounts or effect re-runs
          if (sttServiceRef.current && ttsCompleteHandlerRef.current) {
            sttServiceRef.current.off('ttsQueueCompleted', ttsCompleteHandlerRef.current)
            ttsCompleteHandlerRef.current = null
          }
        }
      }
      // Not speaking, exit immediately
      onExit()
    }
    return undefined
  }, [voiceRateLimitState?.rateLimited, onExit, isSpeakingState, isTranslatingTTS])

  // Compute voice mode state using pure function
  const voiceModeState = useMemo(() => {
    const inputs = createVoiceModeInputs({
      isListening,
      isWaitingForResponseFromAi,
      isMuted,
      isAiSpeaking: isSpeakingState,
      isTranslatingSTT,
      userIsTalking,
      isTranslatingTTS,
    })

    return computeVoiceModeState(inputs, translations)
  }, [
    isListening,
    isWaitingForResponseFromAi,
    isMuted,
    isSpeakingState,
    isTranslatingSTT,
    userIsTalking,
    isTranslatingTTS,
    translations,
  ])

  // Extract computed values for easier access
  const {
    processingUserResponse,
    availableForInput,
    currentHeaderText,
    currentSubheaderText,
    mode: voiceMode,
  } = voiceModeState
  const chatStreamingTextRef = useRef<string>('')
  const accumulatedTextRef = useRef<string>('')
  const hasSeenLinebreakRef = useRef<boolean>(false)
  const sttServiceRef = useRef<STTTTSService | null>(null)
  const auth = useSelector(selectAuth)
  const language = useSelector(selectPrimaryLanguage) || EN
  const dispatch = useDispatch()
  const trackSpeechToText = usePostHogEvent('ai_speech_to_text')

  // Simulate speech-to-text processing for debugging
  const simulateSpeechInput = useCallback((text: string) => {
    // Update UI state for temporary display (same as real STT)
    setLastReceivedText(text)
    setReceivedTexts((prev) => [...prev.slice(-4), text]) // Keep last 5 texts

    // Start GraphQL subscription for AI response
    setIsWaitingForResponseFromAi(true)
    // Reset accumulation for new response
    chatStreamingTextRef.current = ''
    setAccumulatedText('')

    const subscriptionInputData = createOracleSubscriptionInput(
      text,
      personalityId || DEFAULT_PERSONALITY_ID, // Use passed personality ID or fallback
      localSessionIdRef.current || null, // Use ref for immediate access to current session ID
      CHAT_MODE.VOICE, // Voice mode for voice chat
      false, // deepLinks
      userTimezone, // User's timezone for rate limiting
    )

    // Start the GraphQL subscription for AI response
    setSubscriptionInput(subscriptionInputData)
  }, [dispatch, personalityId, userTimezone])

  // Generic error handler that shows toast and captures debug info
  const handleVoiceError = useCallback((debugMessage?: string) => {
    setShowErrorToast(true)
    // Clear any existing voice error state
    setVoiceError(null)
    // Capture technical error details for debug display
    if (debugMessage) {
      setDebugErrors((prev) => {
        const timestamp = new Date().toLocaleTimeString()
        const errorWithTime = `[${timestamp}] ${debugMessage}`
        // Keep last 10 errors to prevent memory issues
        return [errorWithTime, ...prev.slice(0, 9)]
      })
    }
    // Reset any ongoing operations
    setIsWaitingForResponseFromAi(false)
    setSubscriptionInput(null)
  }, [])

  // GraphQL subscription moved to onData callback above

  // No more lazy query or processTTS - we use chunked TTS only

  // Check if debug mode is enabled via query string
  const isDebugMode = useMemo(() => {
    if (location?.search) {
      const urlParams = new URLSearchParams(location.search)
      return urlParams.get('debug') === 'true'
    }
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('debug') === 'true'
    }
    return false
  }, [location?.search])

  // Voice configuration with query string overrides
  const voiceConfig = useMemo(() => {
    const getQueryParams = () => {
      if (location?.search) {
        return new URLSearchParams(location.search)
      }
      if (typeof window !== 'undefined') {
        return new URLSearchParams(window.location.search)
      }
      return new URLSearchParams()
    }

    const urlParams = getQueryParams()

    // Check if webview=true query param is present. We use this to target the ios and android apps
    const isWebview = urlParams.get('webview') === 'true'

    // Parse query string parameters with fallbacks to default config
    const {
      fullDuplex: defaultFullDuplex,
      silenceThreshold: defaultSilenceThreshold,
      startedTalkingThreshold: defaultStartedTalkingThreshold,
    } = VOICE_CONFIG

    const fullDuplexParam = urlParams.get('fullduplex')
    const getFullDuplex = () => {
      if (fullDuplexParam === 'false') return false
      if (fullDuplexParam === 'true') return true
      return defaultFullDuplex
    }
    const fullDuplex = getFullDuplex()

    const silenceThresholdParam = urlParams.get('silencethreshold')
    const silenceThreshold = silenceThresholdParam
      ? parseInt(silenceThresholdParam, 10)
      : defaultSilenceThreshold

    const startedTalkingThresholdParam = urlParams.get('startedtalkingthreshold')
    let startedTalkingThreshold
    if (startedTalkingThresholdParam) {
      startedTalkingThreshold = parseInt(startedTalkingThresholdParam, 10)
    } else {
      startedTalkingThreshold = isWebview
        ? DEFAULT_MOBILE_TALKING_THRESHOLD
        : defaultStartedTalkingThreshold
    }

    // Use mobile threshold as fallback if parsed value is NaN
    let finalStartedTalkingThreshold
    if (Number.isNaN(startedTalkingThreshold)) {
      finalStartedTalkingThreshold = isWebview
        ? DEFAULT_MOBILE_TALKING_THRESHOLD
        : defaultStartedTalkingThreshold
    } else {
      finalStartedTalkingThreshold = startedTalkingThreshold
    }

    return {
      fullDuplex,
      silenceThreshold: Number.isNaN(silenceThreshold)
        ? defaultSilenceThreshold
        : silenceThreshold,
      startedTalkingThreshold: finalStartedTalkingThreshold,
    }
  }, [location?.search])

  const { fullDuplex, silenceThreshold, startedTalkingThreshold } = voiceConfig
  // Use Apollo subscription with direct text accumulation - same as text chat
  const subscriptionResult = useOracleAgentChatSubscriptionSubscription({
    variables: {
      input: subscriptionInput!,
      language,
    },
    skip: !subscriptionInput,
    context: {
      jwt: auth.get('jwt'),
    },
    onData: useCallback(({ data: subscriptionData }) => {
      // Smart accumulation of chunks into newline-delimited segments
      const textChunk = subscriptionData?.data?.oracleAgentChat?.textChunk || ''

      if (textChunk && sttServiceRef.current) {
        try {
          // Don't strip markdown - just use raw text to preserve spaces
          const cleanChunk = textChunk

          // Process chunk for TTS

          // Accumulate and chunk: use punctuation until first linebreak, then linebreak-only
          setAccumulatedText((prev) => {
            // Simple concatenation - backend provides proper spacing
            const newAccumulated = prev + cleanChunk

            // Determine if we should send a chunk based on newline or final flag
            const isFinal = subscriptionData?.data?.oracleAgentChat?.isFinal
            const lastNewlineIndex = Math.max(
              newAccumulated.lastIndexOf('\n'),
              newAccumulated.lastIndexOf('\r'),
            )

            // If a newline is present (or was seen earlier), switch to newline-only mode
            if (lastNewlineIndex >= 0) {
              hasSeenLinebreakRef.current = true
            }

            if (sttServiceRef.current) {
              // After first newline: chunk by newline only
              if (hasSeenLinebreakRef.current) {
                if (lastNewlineIndex >= 0) {
                  const textToSend = newAccumulated.substring(0, lastNewlineIndex + 1)
                  const remaining = newAccumulated.substring(lastNewlineIndex + 1)
                  if (textToSend.trim().length > 0) {
                    // Avoid sending single-word chunks unless this is final
                    if (getWordCount(textToSend) >= MIN_WORDS_PER_CHUNK) {
                      sttServiceRef.current.appendTextToQueue(cleanTextForTTS(textToSend))
                      const trimmedRemaining = remaining.trim()
                      accumulatedTextRef.current = trimmedRemaining
                      return trimmedRemaining
                    }
                    // Too short (single word) - keep buffering
                    accumulatedTextRef.current = newAccumulated
                    return newAccumulated
                  }
                  const trimmedRemaining = remaining.trim()
                  accumulatedTextRef.current = trimmedRemaining
                  return trimmedRemaining
                }

                if (isFinal) {
                  const textToSend = newAccumulated
                  if (textToSend.trim().length > 0) {
                    sttServiceRef.current.appendTextToQueue(cleanTextForTTS(textToSend))
                  }
                  accumulatedTextRef.current = ''
                  return ''
                }

                accumulatedTextRef.current = newAccumulated
                return newAccumulated
              }

              // Before first newline: chunk by punctuation for faster time-to-first-speech
              const lastSentenceEnd = Math.max(
                newAccumulated.lastIndexOf('.'),
                newAccumulated.lastIndexOf('!'),
                newAccumulated.lastIndexOf('?'),
              )
              const lastComma = newAccumulated.lastIndexOf(',')

              if (lastSentenceEnd >= 0) {
                const textToSend = newAccumulated.substring(0, lastSentenceEnd + 1)
                const remaining = newAccumulated.substring(lastSentenceEnd + 1)
                if (textToSend.trim().length > 0) {
                  if (getWordCount(textToSend) >= MIN_WORDS_PER_CHUNK) {
                    sttServiceRef.current.appendTextToQueue(cleanTextForTTS(textToSend))
                    const trimmedRemaining = remaining.trim()
                    accumulatedTextRef.current = trimmedRemaining
                    return trimmedRemaining
                  }
                  // Too short (single word) - keep buffering
                  accumulatedTextRef.current = newAccumulated
                  return newAccumulated
                }
                const trimmedRemaining = remaining.trim()
                accumulatedTextRef.current = trimmedRemaining
                return trimmedRemaining
              }

              if (lastComma >= 0) {
                const textToSend = newAccumulated.substring(0, lastComma + 1)
                const remaining = newAccumulated.substring(lastComma + 1)
                if (textToSend.trim().length > 0) {
                  if (getWordCount(textToSend) >= MIN_WORDS_PER_CHUNK) {
                    sttServiceRef.current.appendTextToQueue(cleanTextForTTS(textToSend))
                    const trimmedRemaining = remaining.trim()
                    accumulatedTextRef.current = trimmedRemaining
                    return trimmedRemaining
                  }
                  // Too short (single word) - keep buffering
                  accumulatedTextRef.current = newAccumulated
                  return newAccumulated
                }
                const trimmedRemaining = remaining.trim()
                accumulatedTextRef.current = trimmedRemaining
                return trimmedRemaining
              }

              if (isFinal) {
                const textToSend = newAccumulated
                if (textToSend.trim().length > 0) {
                  sttServiceRef.current.appendTextToQueue(cleanTextForTTS(textToSend))
                }
                accumulatedTextRef.current = ''
                return ''
              }
            }

            accumulatedTextRef.current = newAccumulated
            return newAccumulated
          })
        } catch (ttsError) {
          // eslint-disable-next-line no-console
          console.error('🎵 TTS Chunk Error:', ttsError)
          handleVoiceError(`TTS Chunk Error: ${ttsError.message}`)
        }
      }

      // Also accumulate for fallback (same as text chat)
      chatStreamingTextRef.current += textChunk

      // Handle chat session ID immediately when received
      const oracleAgentChat = subscriptionData?.data?.oracleAgentChat
      if (oracleAgentChat?.chatSessionId) {
        // Update ref to maintain conversation continuity
        localSessionIdRef.current = oracleAgentChat.chatSessionId

        // Notify parent component of new session
        if (onSessionReceived) {
          onSessionReceived(oracleAgentChat.chatSessionId)
        }
      }

      // Handle final response
      if (oracleAgentChat?.isFinal) {
        // Ensure any final, short chunk begins playback immediately
        try {
          const svc = sttServiceRef.current
          if (svc) {
            // If there's any leftover accumulated text (no newline case), push it now
            const leftover = accumulatedTextRef.current
            if (leftover && leftover.trim().length > 0) {
              svc.appendTextToQueue(cleanTextForTTS(leftover))
              accumulatedTextRef.current = ''
            }

            // Playback will be started by appendTextToQueue if needed
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('TTS flush on final failed (non-fatal):', e)
        }

        // Reset streaming state
        chatStreamingTextRef.current = ''
        setAccumulatedText('')
        setIsWaitingForResponseFromAi(false)
        setSubscriptionInput(null)

        // Refetch voice rate limit after message completes
        // This ensures the count is updated after the message is persisted
        if (voiceRateLimitState?.refetchRateLimit) {
          setTimeout(() => {
            voiceRateLimitState.refetchRateLimit()
          }, 1000) // Short delay to ensure message is persisted to database
        }
      }
    }, [onSessionReceived, handleVoiceError, voiceRateLimitState]),
  })

  const { error: subscriptionError } = subscriptionResult

  // Handle subscription errors
  useEffect(() => {
    if (subscriptionError) {
      // eslint-disable-next-line no-console
      console.error('Subscription Error:', subscriptionError)
      handleVoiceError(`Subscription Error: ${subscriptionError.message}`)
    }
  }, [subscriptionError, handleVoiceError])

  // Initialize STT service when component mounts
  useEffect(() => {
    if (!sttServiceRef.current) {
      sttServiceRef.current = new STTTTSService()

      // Set up event listeners
      sttServiceRef.current.on('sttTextReceived', (text: string) => {
        // Don't process STT text when muted
        if (isMuted) {
          return
        }

        // Update UI state for temporary display
        setLastReceivedText(text)
        setReceivedTexts((prev) => [...prev.slice(-4), text]) // Keep last 5 texts

        // Track speech-to-text GA4 event
        dispatch(setAiGa4Event(AI_SPEECH_TO_TEXT_GA4_EVENT))
        trackSpeechToText()

        // Track voice-specific message sent GA4 event
        dispatch(setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
          ui_element: 'Voice Mode',
          input_method: 'text spoken',
        })))

        // Start GraphQL subscription for AI response
        setIsWaitingForResponseFromAi(true)
        // Reset accumulation for new response
        chatStreamingTextRef.current = ''
        setAccumulatedText('')

        const subscriptionInputData = createOracleSubscriptionInput(
          text,
          personalityId || DEFAULT_PERSONALITY_ID, // Use passed personality ID or fallback
          localSessionIdRef.current || null, // Use ref for immediate access to current session ID
          CHAT_MODE.VOICE, // Voice mode for voice chat
          false, // deepLinks
          userTimezone, // User's timezone for rate limiting
        )

        // Start the GraphQL subscription for AI response
        setSubscriptionInput(subscriptionInputData)
      })

      sttServiceRef.current.on('error', (message: string, serviceError?: unknown) => {
        // eslint-disable-next-line no-console
        console.error('STT-TTS Service Error:', message, serviceError)
        handleVoiceError(`STT-TTS Service Error: ${message}`)
      })

      sttServiceRef.current.on('stateChanged', (state: {
        isListening: boolean;
        isHearingUser: boolean;
        isTranslatingTTS: boolean;
        isTranslatingSTT?: boolean;
        userIsTalking?: boolean;
        isSpeakingState?: boolean;
      }) => {
        // Update STT service states - let the voice mode state calculation handle mute logic
        setIsTranslatingSTT(state.isTranslatingSTT || false)
        setUserIsTalking(state.userIsTalking || state.isHearingUser || false)
        setIsTranslatingTTS(state.isTranslatingTTS || false)
        setIsSpeakingState(state.isSpeakingState || false)
        setIsListening(state.isListening || state.isHearingUser)
      })

      // Add listeners for TTS chunk events to maintain speaking state
      // This ensures the UI shows "AI is speaking" consistently during chunked TTS processing
      sttServiceRef.current.on('ttsChunkStarted', () => {
        // Ensure speaking state is true when any chunk starts
        setIsSpeakingState(true)
      })

      sttServiceRef.current.on('ttsChunkCompleted', () => {
        // Note: Don't set isSpeakingState to false here as there might be more chunks
        // The service will handle this via stateChanged event
      })

      sttServiceRef.current.on('ttsQueueCompleted', () => {
        // The entire TTS queue is finished - speaking state should be false
        setIsSpeakingState(false)
      })
    }

    // Initialize audio system when dependencies become available
    const jwtToken = auth.get('jwt')

    if (sttServiceRef.current && speechServer && speechServer.trim() !== '' && jwtToken && !sttServiceRef.current.isAudioSystemInitialized) {
      try {
        // Check if AudioContext is supported
        const windowWithWebkit = window as unknown as { webkitAudioContext?: typeof AudioContext }
        const hasWebkitAudioContext = windowWithWebkit.webkitAudioContext
        if (!window.AudioContext && !hasWebkitAudioContext) {
          throw new Error('AudioContext not supported in this browser')
        }

        sttServiceRef.current.initialize(jwtToken, speechServer)
      } catch (initError: unknown) {
        // eslint-disable-next-line no-console
        console.error('STT Service initialization failed:', initError)
        const errorMessage = initError instanceof Error ? initError.message : 'Unknown initialization error'
        handleVoiceError(`STT Service initialization failed: ${errorMessage}`)
      }
    }

    return () => {
      // Clean up when component unmounts (voice mode exits)
      if (sttServiceRef.current) {
        sttServiceRef.current.teardownAudio()
      }
    }
  }, [speechServer, auth.get('jwt'), handleVoiceError])

  // Start listening when voice mode is active
  useEffect(() => {
    const startVoiceMode = async () => {
      if (!sttServiceRef.current) {
        handleVoiceError('Voice service not available')
        return
      }

      // Check if the main audio system is initialized
      if (!sttServiceRef.current.isAudioSystemInitialized) {
        handleVoiceError('Audio system not ready. Please wait a moment.')
        return
      }

      try {
        setVoiceError(null)

        // Initialize reusable audio first if not already done
        if (!sttServiceRef.current.isReusableAudioInitialized) {
          sttServiceRef.current.initializeReusableAudio(
            fullDuplex,
            silenceThreshold,
            startedTalkingThreshold,
            language,
          )
        }

        // Start the conversation (listening)
        await sttServiceRef.current.startConversation()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Voice mode start error:', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to start voice mode'
        handleVoiceError(`Voice mode start error: ${errorMessage}`)
      }
    }

    // Only start if we have a valid service and dependencies
    const jwtToken = auth.get('jwt')
    if (speechServer && speechServer.trim() !== '' && jwtToken) {
      startVoiceMode()
    }

    return () => {
      // Stop conversation when effect cleans up
      if (sttServiceRef.current) {
        sttServiceRef.current.stopConversation()
      }
    }
  }, [speechServer, auth.get('jwt'), fullDuplex, silenceThreshold, startedTalkingThreshold, language, handleVoiceError])

  const handleExit = () => {
    // Stop the conversation before exiting
    if (sttServiceRef.current) {
      sttServiceRef.current.stopConversation()
    }
    if (onExit) {
      onExit()
    }
  }

  const handleMuteToggle = useCallback(async () => {
    const newMutedState = !isMuted
    setIsMuted(newMutedState)

    if (!sttServiceRef.current) {
      handleVoiceError('Voice service not available')
      return
    }

    if (newMutedState) {
      // Muting - stop only STT, keep TTS active
      try {
        await sttServiceRef.current.stopListening()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to stop listening:', error)
        handleVoiceError(`Failed to mute: ${error.message}`)
      }
    } else {
      // Unmuting - restart only STT
      if (!sttServiceRef.current.isAudioSystemInitialized) {
        handleVoiceError('Audio system not ready. Please wait a moment.')
        setIsMuted(true) // Revert to muted state
        return
      }

      try {
        await sttServiceRef.current.startListening()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to start listening:', error)
        handleVoiceError(`Failed to unmute: ${error.message}`)
        setIsMuted(true) // Revert to muted state on error
      }
    }
  }, [isMuted, handleVoiceError])

  return (
    <div className="oracle-voice-chat">
      <div className="oracle-voice-chat__content">
        <div className="oracle-voice-chat__top-section">
          <div className="oracle-voice-chat__ai-icon">
            <OracleVoiceModeGlowIcon />
          </div>

          <div className="oracle-voice-chat__text-container">
            <div className="oracle-voice-chat__text-content">
              <div className="oracle-voice-chat__voice-mode-header">
                {currentHeaderText}
              </div>
              {currentSubheaderText && (
                <div className="oracle-voice-chat__voice-mode-subheader">
                  {currentSubheaderText}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="oracle-voice-chat__buttons-container">
          <div className="oracle-voice-chat__microphone-button">
            <OracleMicrophoneButton
              isMuted={isMuted}
              onClick={handleMuteToggle}
              disabled={!sttServiceRef.current?.isAudioSystemInitialized}
            />
          </div>

          {onExit && (
            <div className="oracle-voice-chat__exit-button">
              <button
                onClick={handleExit}
                className="oracle-exit-button"
                aria-label={exitVoiceChatLabel}
              >
                {/* Using square SVG icon to match Figma */}
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 8L24 24M24 8L8 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Tooltip message below the buttons */}
        {showTooltip && (
          <div className="oracle-voice-chat__tooltip">
            <div className="oracle-voice-chat__tooltip-icon"></div>
            <span className="oracle-voice-chat__tooltip-text">
              {muteTooltipText}
            </span>
            <button
              onClick={() => setShowTooltip(false)}
              className="oracle-voice-chat__tooltip-close"
              aria-label={closeTooltipLabel}
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Debug info accordion at the bottom - only show if debug=true in query string */}
      {isDebugMode && (
        <div className={`oracle-voice-chat__debug-container ${showDebugInfo ? 'oracle-voice-chat__debug-container--expanded' : ''}`}>
          <button
            onClick={() => setShowDebugInfo(!showDebugInfo)}
            className="oracle-voice-chat__debug-toggle"
          >
            <span>{showDebugInfo ? '▼' : '▶'}</span>
            Debug Information {showDebugInfo ? '(Hide)' : '(Show)'}
          </button>

          {/* Accordion content */}
          {showDebugInfo && (
            <div className="oracle-voice-chat__debug-accordion">
              <h2>Voice Mode - Full Screen</h2>
              <p>Voice mode is active. Start speaking to interact with the AI.</p>

              {/* Voice Configuration Display */}
              <div className="oracle-voice-chat__debug-config">
                <h3>Voice Configuration</h3>
                <div className="oracle-voice-chat__debug-config-items">
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Full Duplex:</strong> {fullDuplex ? 'true' : 'false'}
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Silence Threshold:</strong> {silenceThreshold}ms
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Started Talking Threshold:</strong> {startedTalkingThreshold}ms
                  </div>
                </div>
                <p className="oracle-voice-chat__debug-config-note">
                  Override with URL params:
                  ?fullduplex=true&amp;silencethreshold=2000&amp;startedtalkingthreshold=50
                </p>
              </div>

              {/* Debug Control Buttons */}
              <div className="oracle-voice-chat__debug-controls">
                <button
                  onClick={() => handleVoiceError('Simulated error for testing purposes')}
                  className="oracle-voice-chat__debug-button"
                >
                  Simulate Error
                </button>
                <button
                  onClick={() => simulateSpeechInput('What is yoga?')}
                  className="oracle-voice-chat__debug-button"
                >
                  Simulate &quot;What is yoga?&quot;
                </button>
                <button
                  onClick={() => simulateSpeechInput('How can you help me?')}
                  className="oracle-voice-chat__debug-button"
                >
                  Simulate &quot;How can you help me?&quot;
                </button>
              </div>
              {isListening && <p>🎤 Listening...</p>}
              {isWaitingForResponseFromAi && <p>⏳ Waiting for AI response...</p>}
              {isSpeakingState && <p>🔊 AI is speaking...</p>}
              {isTranslatingTTS && <p>🔄 Converting text to speech...</p>}
              {/* New state indicators */}
              <div className="oracle-voice-chat__debug-states">
                <h4>Voice Mode States:</h4>
                <div className="oracle-voice-chat__debug-config-items">
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Voice Mode:</strong> {voiceMode}
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Processing User Response:</strong> {processingUserResponse ? 'true' : 'false'}
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Available For Input:</strong> {availableForInput ? 'true' : 'false'}
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>User Is Talking:</strong> {userIsTalking ? 'true' : 'false'}
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Is Translating STT:</strong> {isTranslatingSTT ? 'true' : 'false'}
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Is Translating TTS:</strong> {isTranslatingTTS ? 'true' : 'false'}
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Is Muted:</strong> {isMuted ? 'true' : 'false'}
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Is Speaking State:</strong> {isSpeakingState ? 'true' : 'false'}
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Current Header:</strong> &quot;{currentHeaderText}&quot;
                  </div>
                  <div className="oracle-voice-chat__debug-config-item">
                    <strong>Current Subheader:</strong> {currentSubheaderText ? `"${currentSubheaderText}"` : 'null'}
                  </div>
                </div>
              </div>
              {voiceError && (
                <div className="oracle-voice-chat__error">
                  <span>{voiceError}</span>
                </div>
              )}

              {/* Display AI Response - using chunked TTS now */}
              <div className="oracle-voice-chat__ai-response">
                <h3>
                  AI Response: {isSpeakingState ? '🔊 Speaking...' : '✅ Completed'}
                </h3>
                <div className="oracle-voice-chat__ai-response-content">
                  <div><strong>Accumulated Text:</strong> {accumulatedText || 'None'}</div>
                  <div><strong>Mode:</strong> Chunked TTS processing</div>
                </div>
              </div>

              {/* Temporary display for STT text */}
              <div className="oracle-voice-chat__debug">
                <h3>
                  Voice Input Debug (Temporary)
                </h3>

                {lastReceivedText && (
                  <div className="oracle-voice-chat__debug-last-received">
                    <strong>Last Received:</strong>
                    <div className="oracle-voice-chat__debug-text">
                      &quot;{lastReceivedText}&quot;
                    </div>
                  </div>
                )}

                {receivedTexts.length > 0 && (
                  <div className="oracle-voice-chat__debug-recent-texts">
                    <strong>Recent Texts Sent to AI:</strong>
                    <div className="oracle-voice-chat__debug-recent-list">
                      {receivedTexts.map((text, index) => (
                        <div
                          key={index}
                          className="oracle-voice-chat__debug-recent-item"
                        >
                          {index + 1}. &quot;{text}&quot;
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {receivedTexts.length === 0 && (
                  <p className="oracle-voice-chat__debug-empty-state">
                    No voice input received yet. Try speaking something!
                  </p>
                )}
              </div>

              {/* Debug Error Log */}
              <div className="oracle-voice-chat__debug-errors">
                <h3>Technical Error Log</h3>
                {debugErrors.length > 0 ? (
                  <div className="oracle-voice-chat__debug-error-list">
                    {debugErrors.map((error, index) => (
                      <div
                        key={index}
                        className="oracle-voice-chat__debug-error-item"
                      >
                        {error}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="oracle-voice-chat__debug-empty-state">
                    No errors logged yet.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <OracleErrorToast
        isVisible={showErrorToast}
        onClose={() => setShowErrorToast(false)}
      />
    </div>
  )
}

export default OracleVoiceChat
